import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  api,
  ApiError,
  type ErrorLogEntry,
  type ErrorLogFacetMachine,
  type ErrorLogFacets,
  type ErrorLogLevel,
  type ErrorLogOrigin,
} from "../api.js";
import { useToken } from "../auth.js";
import { Badge } from "../components/Badge.js";

// All origins/levels — displayed as multi-select chips.
const ALL_ORIGINS: ErrorLogOrigin[] = ["cloud-api", "local-agent", "mcp-server"];
const ALL_LEVELS: ErrorLogLevel[] = ["error", "warn"];

// Time range presets in minutes.
const TIME_PRESETS: Array<{ label: string; minutes: number }> = [
  { label: "1h", minutes: 60 },
  { label: "24h", minutes: 60 * 24 },
  { label: "7d", minutes: 60 * 24 * 7 },
  { label: "30d", minutes: 60 * 24 * 30 },
];

const PAGE_SIZE = 100;

interface Filters {
  origin: ErrorLogOrigin[];
  level: ErrorLogLevel[];
  customerId: string | null;
  machineId: string | null;
  sinceMinutes: number;
  q: string;
}

const DEFAULT_FILTERS: Filters = {
  origin: [],
  level: [],
  customerId: null,
  machineId: null,
  sinceMinutes: 60 * 24,
  q: "",
};

export function Errors() {
  const token = useToken();
  const { t, i18n } = useLingui();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<Filters>(() => filtersFromParams(searchParams));
  const [debouncedQ, setDebouncedQ] = useState(filters.q);
  const [rows, setRows] = useState<ErrorLogEntry[]>([]);
  const [nextCursor, setNextCursor] = useState<{ loggedAt: string; id: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [facets, setFacets] = useState<ErrorLogFacets | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  // Debounce text search — server hit at most once per 300ms.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(filters.q), 300);
    return () => clearTimeout(t);
  }, [filters.q]);

  // Reflect the current filters into the URL so links are shareable.
  useEffect(() => {
    setSearchParams(paramsFromFilters({ ...filters, q: debouncedQ }), { replace: true });
  }, [filters, debouncedQ, setSearchParams]);

  const load = useCallback(async () => {
    setLoadError(null);
    setLoading(true);
    try {
      const res = await api.listErrors(token, {
        origin: filters.origin.length > 0 ? filters.origin : undefined,
        level: filters.level.length > 0 ? filters.level : undefined,
        customerId: filters.customerId ?? undefined,
        machineId: filters.machineId ?? undefined,
        sinceMinutes: filters.sinceMinutes,
        q: debouncedQ || undefined,
        limit: PAGE_SIZE,
      });
      setRows(res.errors);
      setNextCursor(res.nextCursor);
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [
    token,
    filters.origin,
    filters.level,
    filters.customerId,
    filters.machineId,
    filters.sinceMinutes,
    debouncedQ,
  ]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void api
      .errorFacets(token)
      .then(setFacets)
      .catch(() => setFacets({ origins: [], customers: [], machines: [] }));
  }, [token]);

  const loadMore = useCallback(async () => {
    if (!nextCursor) return;
    setLoadingMore(true);
    try {
      const res = await api.listErrors(token, {
        origin: filters.origin.length > 0 ? filters.origin : undefined,
        level: filters.level.length > 0 ? filters.level : undefined,
        customerId: filters.customerId ?? undefined,
        machineId: filters.machineId ?? undefined,
        sinceMinutes: filters.sinceMinutes,
        q: debouncedQ || undefined,
        limit: PAGE_SIZE,
        cursor: nextCursor,
      });
      setRows((prev) => [...prev, ...res.errors]);
      setNextCursor(res.nextCursor);
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoadingMore(false);
    }
  }, [
    token,
    nextCursor,
    filters.origin,
    filters.level,
    filters.customerId,
    filters.machineId,
    filters.sinceMinutes,
    debouncedQ,
  ]);

  const machineOptions = useMemo<ErrorLogFacetMachine[]>(() => {
    if (!facets) return [];
    if (!filters.customerId) return facets.machines;
    return facets.machines.filter((m) => m.customerId === filters.customerId);
  }, [facets, filters.customerId]);

  const activeFilterCount = countActiveFilters(filters);
  const selectedCustomerName = filters.customerId
    ? facets?.customers.find((c) => c.customerId === filters.customerId)?.customerName ?? null
    : null;
  const selectedMachineName = filters.machineId
    ? facets?.machines.find((m) => m.machineId === filters.machineId)?.machineName ??
      filters.machineId.slice(0, 8) + "…"
    : null;
  const currentTimeLabel =
    TIME_PRESETS.find((p) => p.minutes === filters.sinceMinutes)?.label ?? `${filters.sinceMinutes}m`;

  return (
    <div>
      <header className="page-header">
        <h1><Trans>Errors</Trans></h1>
        <div className="row-actions">
          <button
            className="ghost"
            onClick={() => setFilters(DEFAULT_FILTERS)}
            disabled={activeFilterCount === 0}
            title={t`Clear all filters`}
          >
            <Trans>Reset</Trans>{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
          </button>
          <button className="ghost" onClick={() => void load()} disabled={loading}>
            {loading ? <Trans>Refreshing…</Trans> : <Trans>Refresh</Trans>}
          </button>
        </div>
      </header>

      {loadError && <div className="banner err">{loadError}</div>}

      <section className="errors-filters">
        <div className="errors-search">
          <span className="errors-search-icon" aria-hidden="true">
            <SearchIcon />
          </span>
          <input
            type="text"
            value={filters.q}
            placeholder={t`Search message, source or error code…`}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
          />
          {filters.q && (
            <button
              className="errors-search-clear"
              onClick={() => setFilters((f) => ({ ...f, q: "" }))}
              aria-label={t`Clear search`}
              type="button"
            >
              ×
            </button>
          )}
        </div>

        <div className="errors-filter-grid">
          <div className="errors-filter-group">
            <span className="errors-filter-label"><Trans>Time range</Trans></span>
            <div className="chips">
              {TIME_PRESETS.map((p) => (
                <button
                  key={p.minutes}
                  className={filters.sinceMinutes === p.minutes ? "chip active" : "chip"}
                  onClick={() => setFilters((f) => ({ ...f, sinceMinutes: p.minutes }))}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div className="errors-filter-group">
            <span className="errors-filter-label"><Trans>Level</Trans></span>
            <div className="chips">
              {ALL_LEVELS.map((l) => (
                <button
                  key={l}
                  className={filters.level.includes(l) ? "chip active" : "chip"}
                  onClick={() =>
                    setFilters((f) => ({
                      ...f,
                      level: toggle(f.level, l),
                    }))
                  }
                >
                  <span className={`level-dot level-dot-${l}`} aria-hidden="true" />
                  {l}
                </button>
              ))}
            </div>
          </div>

          <div className="errors-filter-group errors-filter-group-wide">
            <span className="errors-filter-label"><Trans>Origin</Trans></span>
            <div className="chips">
              {ALL_ORIGINS.map((o) => (
                <button
                  key={o}
                  className={filters.origin.includes(o) ? "chip active" : "chip"}
                  onClick={() =>
                    setFilters((f) => ({
                      ...f,
                      origin: toggle(f.origin, o),
                    }))
                  }
                >
                  {o}
                </button>
              ))}
            </div>
          </div>

          <div className="errors-filter-select">
            <span className="errors-filter-label"><Trans>Customer</Trans></span>
            <select
              value={filters.customerId ?? ""}
              onChange={(e) =>
                setFilters((f) => ({
                  ...f,
                  customerId: e.target.value || null,
                  machineId: null,
                }))
              }
            >
              <option value="">{t`All customers`}</option>
              {facets?.customers.map((c) => (
                <option key={c.customerId} value={c.customerId}>
                  {c.customerName} ({c.count})
                </option>
              ))}
            </select>
          </div>

          <div className="errors-filter-select">
            <span className="errors-filter-label"><Trans>Machine</Trans></span>
            <select
              value={filters.machineId ?? ""}
              onChange={(e) =>
                setFilters((f) => ({ ...f, machineId: e.target.value || null }))
              }
              disabled={machineOptions.length === 0}
            >
              <option value="">{t`All machines`}</option>
              {machineOptions.map((m) => (
                <option key={m.machineId} value={m.machineId}>
                  {m.machineName ?? m.machineId.slice(0, 8) + "…"} — {m.customerName} ({m.count})
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      <div className="errors-summary">
        <div className="errors-result-count">
          {loading ? (
            <span className="muted"><Trans>Loading…</Trans></span>
          ) : (
            <>
              <strong>{rows.length}</strong>
              <span className="muted">
                {" "}<Trans>error(s)</Trans>{nextCursor ? "+" : ""} · <Trans>last {currentTimeLabel}</Trans>
              </span>
            </>
          )}
        </div>
        {activeFilterCount > 0 && (
          <div className="errors-active-chips">
            {selectedCustomerName && (
              <ActivePill
                label={t`Customer: ${selectedCustomerName}`}
                onClear={() =>
                  setFilters((f) => ({ ...f, customerId: null, machineId: null }))
                }
              />
            )}
            {selectedMachineName && (
              <ActivePill
                label={t`Machine: ${selectedMachineName}`}
                onClear={() => setFilters((f) => ({ ...f, machineId: null }))}
              />
            )}
            {filters.origin.map((o) => (
              <ActivePill
                key={`o-${o}`}
                label={o}
                onClear={() =>
                  setFilters((f) => ({ ...f, origin: f.origin.filter((x) => x !== o) }))
                }
              />
            ))}
            {filters.level.map((l) => (
              <ActivePill
                key={`l-${l}`}
                label={l}
                onClear={() =>
                  setFilters((f) => ({ ...f, level: f.level.filter((x) => x !== l) }))
                }
              />
            ))}
            {debouncedQ && (
              <ActivePill
                label={`"${truncate(debouncedQ, 24)}"`}
                onClear={() => setFilters((f) => ({ ...f, q: "" }))}
              />
            )}
          </div>
        )}
      </div>

      <section>
        {loading ? (
          <p className="muted"><Trans>Loading…</Trans></p>
        ) : rows.length === 0 ? (
          <div className="errors-empty">
            <p className="muted"><Trans>No errors match these filters.</Trans></p>
            {activeFilterCount > 0 && (
              <button
                className="link-btn"
                onClick={() => setFilters(DEFAULT_FILTERS)}
              >
                <Trans>Clear filters</Trans>
              </button>
            )}
          </div>
        ) : (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th><Trans>When</Trans></th>
                  <th><Trans>Origin</Trans></th>
                  <th><Trans>Level</Trans></th>
                  <th><Trans>Machine</Trans></th>
                  <th><Trans>Customer</Trans></th>
                  <th><Trans>Source</Trans></th>
                  <th><Trans>Message</Trans></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const isOpen = expanded === r.id;
                  return (
                    <Fragment key={r.id}>
                      <tr
                        onClick={() => setExpanded(isOpen ? null : r.id)}
                        className={isOpen ? "row-expanded" : "row-clickable"}
                      >
                        <td className="mono">{new Date(r.loggedAt).toLocaleString(i18n.locale)}</td>
                        <td>
                          <Badge tone={originTone(r.origin)}>{r.origin}</Badge>
                        </td>
                        <td>
                          <Badge tone={r.level === "error" ? "err" : "warn"}>
                            {r.level}
                          </Badge>
                        </td>
                        <td className="mono">
                          {r.machineId ? r.machineId.slice(0, 8) + "…" : "—"}
                        </td>
                        <td>
                          {r.customerId ? (
                            <Link to={`/customers/${r.customerId}`}>{r.customerId.slice(0, 8) + "…"}</Link>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="mono short-id">{r.source ?? "—"}</td>
                        <td>{truncate(r.message, 160)}</td>
                      </tr>
                      {isOpen && (
                        <tr className="details-row">
                          <td colSpan={7}>
                            <ErrorDetail entry={r} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
            {nextCursor && (
              <div className="load-more">
                <button className="ghost" onClick={() => void loadMore()} disabled={loadingMore}>
                  {loadingMore ? <Trans>Loading…</Trans> : <Trans>Load more</Trans>}
                </button>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

function ErrorDetail({ entry }: { entry: ErrorLogEntry }) {
  const { i18n } = useLingui();
  return (
    <div className="error-detail">
      <div className="kv">
        <div><strong><Trans>Message</Trans></strong></div>
        <div className="mono wrap">{entry.message}</div>
        {entry.errorCode && (
          <>
            <div><strong><Trans>Code</Trans></strong></div>
            <div className="mono">{entry.errorCode}</div>
          </>
        )}
        {entry.requestId && (
          <>
            <div><strong><Trans>Request ID</Trans></strong></div>
            <div className="mono">{entry.requestId}</div>
          </>
        )}
        {entry.agentVersion && (
          <>
            <div><strong><Trans>Agent</Trans></strong></div>
            <div className="mono">{entry.agentVersion}</div>
          </>
        )}
      </div>
      {entry.stack && (
        <details open>
          <summary><Trans>Stack</Trans></summary>
          <pre className="mono wrap">{entry.stack}</pre>
        </details>
      )}
      {entry.context && Object.keys(entry.context).length > 0 && (
        <details>
          <summary><Trans>Context</Trans></summary>
          <pre className="mono wrap">{JSON.stringify(entry.context, null, 2)}</pre>
        </details>
      )}
      <div className="muted short-id">
        <Trans>Received {new Date(entry.receivedAt).toLocaleString(i18n.locale)} · id {entry.id}</Trans>
      </div>
    </div>
  );
}

function SearchIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function ActivePill({ label, onClear }: { label: string; onClear: () => void }) {
  const { t } = useLingui();
  return (
    <button className="active-pill" onClick={onClear} type="button" title={t`Remove filter`}>
      <span>{label}</span>
      <span className="active-pill-x" aria-hidden="true">×</span>
    </button>
  );
}

function countActiveFilters(f: Filters): number {
  let n = 0;
  if (f.origin.length > 0) n += f.origin.length;
  if (f.level.length > 0) n += f.level.length;
  if (f.customerId) n += 1;
  if (f.machineId) n += 1;
  if (f.q.trim().length > 0) n += 1;
  if (f.sinceMinutes !== DEFAULT_FILTERS.sinceMinutes) n += 1;
  return n;
}

function toggle<T>(arr: T[], v: T): T[] {
  return arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

function originTone(o: ErrorLogOrigin): "ok" | "warn" | "err" | "unknown" {
  if (o === "cloud-api") return "warn";
  if (o === "local-agent") return "unknown";
  return "ok";
}

function filtersFromParams(params: URLSearchParams): Filters {
  const origins = params.getAll("origin").filter((o): o is ErrorLogOrigin =>
    ALL_ORIGINS.includes(o as ErrorLogOrigin),
  );
  const levels = params.getAll("level").filter((l): l is ErrorLogLevel =>
    ALL_LEVELS.includes(l as ErrorLogLevel),
  );
  const since = Number(params.get("sinceMinutes") ?? "1440");
  return {
    origin: origins,
    level: levels,
    customerId: params.get("customerId"),
    machineId: params.get("machineId"),
    sinceMinutes: Number.isFinite(since) && since > 0 ? since : 1440,
    q: params.get("q") ?? "",
  };
}

function paramsFromFilters(f: Filters): URLSearchParams {
  const params = new URLSearchParams();
  for (const o of f.origin) params.append("origin", o);
  for (const l of f.level) params.append("level", l);
  if (f.customerId) params.set("customerId", f.customerId);
  if (f.machineId) params.set("machineId", f.machineId);
  params.set("sinceMinutes", String(f.sinceMinutes));
  if (f.q) params.set("q", f.q);
  return params;
}

